// External library imports
import { ArrowLeft, Pencil, Star, Trash } from "lucide-react";
import { useRouter } from "@tanstack/react-router";
import { format } from "date-fns";

// Internal component imports
import { Button } from "@/components/ui/button";

// Hooks and utilities
import { cn } from "@/lib/utils";
import { useConfirm } from "@/hooks/use-confirm";
import { useTodo } from "./queries/todos.get.queries";
import { useToggleTodoStarred } from "./queries/todos.update.queries";
import { useDeleteTodo } from "./queries/todos.delete.queries";
import { toast } from "sonner";
import { Priority } from "./components/priority";
import { Status } from "./components/status";
import { TodoDetailSkeleton } from "./components/detail-skeleton";

// Constants
const DATE_FORMAT = "MMM d, yyyy";

interface TodoDetailPageProps {
  todoId: string;
}

const DetailField = ({
  label,
  value,
  children,
}: {
  label: string;
  value?: string | null;
  children?: React.ReactNode;
}) => (
  <div>
    <label className="text-sm font-medium text-neutral-600">{label}</label>
    <div className="mt-1">{children || <p>{value || "-"}</p>}</div>
  </div>
);

const TodoDetailPage = ({ todoId }: TodoDetailPageProps) => {
  const router = useRouter();
  const { data: todo, isLoading } = useTodo(todoId);
  const { mutateAsync: deleteTodo } = useDeleteTodo();
  const toggleStarred = useToggleTodoStarred(todoId);

  const [ConfirmationDialog, confirm] = useConfirm({
    title: "删除待办",
    message: "确定要删除该待办吗？此操作不可撤销。",
  });

  const handleDelete = async () => {
    try {
      const confirmed = await confirm();
      if (!confirmed) return;

      await deleteTodo(todoId);
      router.navigate({ to: "/dashboard/todos" });
      toast.success("待办删除成功");
    } catch (error) {
      console.error("删除待办错误:", error);
      toast.error("删除待办失败");
    }
  };

  const handleToggleStarred = async () => {
    try {
      await toggleStarred.mutateAsync(todoId);
      toast.success(
        `待办${todo?.data?.isStarred ? "取消标注" : "标注为重要"}成功`,
      );
    } catch (error) {
      console.error("切换待办重要状态错误:", error);
      toast.error("更新待办重要状态失败");
    }
  };

  const handleEdit = () => {
    router.navigate({
      to: "/dashboard/todos/edit/$id",
      params: { id: todoId },
    });
  };

  if (isLoading) return <TodoDetailSkeleton />;

  const todoData = todo?.data;

  return (
    <div className="max-w-2xl">
      <div className="mb-8">
        <div className="mb-8 flex items-center justify-between">
          <h2 className="text-xl font-semibold">待办详情</h2>
          <Button
            variant={todoData?.isStarred ? "default" : "outline"}
            className={cn(
              todoData?.isStarred && "bg-orange-500 hover:bg-orange-600",
            )}
            size="sm"
            onClick={handleToggleStarred}
            aria-label={todoData?.isStarred ? "取消重要标注" : "标注为重要"}
          >
            <Star
              className={cn(
                "size-4",
                todoData?.isStarred && "fill-white text-white",
              )}
            />
            {todoData?.isStarred ? "已标注" : "标注"}
          </Button>
        </div>

        <div className="space-y-6">
          <DetailField label="标题" value={todoData?.title} />
          <DetailField label="状态">
            <Status status={todoData?.status!} size="sm" />
          </DetailField>
          <DetailField label="优先级">
            <Priority priority={todoData?.priority!} size="sm" />
          </DetailField>
          <DetailField
            label="截止日期"
            value={
              todoData?.dueDate
                ? format(new Date(todoData.dueDate), DATE_FORMAT)
                : undefined
            }
          />
          <DetailField
            label="完成时间"
            value={
              todoData?.completedAt
                ? format(new Date(todoData.completedAt), DATE_FORMAT)
                : undefined
            }
          />
          <DetailField label="描述" value={todoData?.description} />

          <div className="mt-8 flex items-center justify-between gap-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => router.navigate({ to: "/dashboard/todos" })}
              aria-label="返回待办列表"
            >
              <ArrowLeft className="h-4 w-4" />
              返回列表
            </Button>
            <div className="flex items-center gap-2">
              <Button
                className="bg-neutral-950 text-white hover:bg-neutral-800"
                onClick={handleEdit}
                aria-label="编辑待办"
              >
                <Pencil className="size-4" />
                编辑
              </Button>
              <Button
                variant="destructive"
                onClick={handleDelete}
                aria-label="删除待办"
              >
                <Trash className="size-4" />
                删除
              </Button>
            </div>
          </div>
        </div>
      </div>
      <ConfirmationDialog />
    </div>
  );
};

export default TodoDetailPage;
